import { ref, computed } from 'vue';

/**
 * Delegated (v3 homepage) — core logic, shared by both portals.
 *
 * Lists the evaluations/tasks the current user invited OTHERS to do, from
 * `POST Report/delegated`: who it's with, progress (step N-of-M), and whether
 * it's stalled. `self` pattern; caller injects an axios-like `http`. Navigation
 * stays in the presentation layer.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => number} [options.getLimit] rows before "View all"
 * @param {() => void} [options.onReady] called once after the first fetch
 */
const SERVER_LIMIT = 100;

export function delegated(options) {
  const { http, getLimit = () => 6, onReady = () => {} } = options || {};
  const limit = () => getLimit() || 6;

  const self = { rows: ref([]), loading: ref(false), showAll: ref(false) };

  self.isLoading = computed(() => self.loading.value);
  self.count = computed(() => self.rows.value.length);
  self.staleCount = computed(() => self.rows.value.filter((r) => self.isStale(r)).length);
  self.shownRows = computed(() => (self.showAll.value ? self.rows.value : self.rows.value.slice(0, limit())));

  self.isStale = (r) => !!r && (r.stale === true || r.stale === 1);
  self.isInProgress = (r) => !!r && r.status_key === 'in-progress';

  self.assigneeName = (r) => (r && r.assignee_name ? String(r.assignee_name).trim() : '');
  self.initials = (name) => {
    const parts = String(name || '').trim().split(/\s+/);
    if (!parts[0]) return '–';
    return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };

  self.totalSteps = (r) => Number(r && r.total_steps) || 0;
  self.completedSteps = (r) => Number(r && r.completed_steps) || 0;
  self.progressPct = (r) => {
    const t = self.totalSteps(r);
    return t ? Math.min(100, Math.round((self.completedSteps(r) / t) * 100)) : 0;
  };
  self.stepText = (r) => (self.totalSteps(r) ? `Step ${self.completedSteps(r)} of ${self.totalSteps(r)}` : '');

  self.statusLabel = (r) => (self.isInProgress(r) ? 'In progress' : 'Not started');
  self.statusColour = (r) =>
    self.isStale(r) ? 'var(--v3-warn, #B27B12)' : self.isInProgress(r) ? 'var(--v3-accent)' : '#5A6678';
  self.actionLabel = (r) => (self.isStale(r) ? 'Chase' : 'View');
  self.actionKind = (r) => 'v3-tt-btn--' + self.actionLabel(r).toLowerCase();

  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };
  self.invitedText = (r) => (r && r.invited_date ? 'invited ' + fmtDate(r.invited_date) : '');
  self.staleText = (r) => (self.isStale(r) ? `No activity for ${Number(r.days_inactive) || 0}d` : '');
  self.rowSub = (r) => [self.assigneeName(r), self.invitedText(r)].filter(Boolean).join(' · ');
  // Prototype V3Delegated row anatomy: head (title + pill), meta (type · who ·
  // invited date), foot (note + Remind-if-stale).
  self.taskType = (r) => (r && r.task_type ? String(r.task_type) : '');
  self.metaParts = (r) => [self.taskType(r), self.assigneeName(r), self.invitedText(r)].filter(Boolean);
  self.note = (r) => (self.isStale(r) ? self.staleText(r) : self.isInProgress(r) ? self.stepText(r) : '');

  async function load() {
    self.loading.value = true;
    try {
      const resp = await http.post('Report/delegated', { limit: SERVER_LIMIT });
      const data = resp && resp.data;
      self.rows.value = Array.isArray(data) ? data : data && Array.isArray(data.Data) ? data.Data : [];
    } catch {
      self.rows.value = [];
    } finally {
      self.loading.value = false;
    }
  }

  let didReady = false;
  Promise.resolve(load()).finally(() => {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  });

  return self;
}
